import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Target, Wand2, Trash2, RefreshCw, Inbox, Printer, Download, Mail, Settings, Link as LinkIcon, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { MetricHeroStats } from '../../components/layout/MetricHeroStats';
import { getLightSectionTheme } from '../../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { value: 'in_progress', label: 'In Progress', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'completed', label: 'Completed', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { value: 'dismissed', label: 'Dismissed', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
];

const statusMeta = (value) => STATUS_OPTIONS.find(s => s.value === value) || STATUS_OPTIONS[0];

// Minimal markdown renderer (same subset used on PEDiagnosticPage).
const MarkdownRenderer = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  for (const line of lines) {
    i++;
    if (line.startsWith('## ')) elements.push(<h3 key={`h3-${i}`} className="text-sm font-semibold text-slate-800 mt-4 mb-2">{line.replace('## ', '')}</h3>);
    else if (line.startsWith('### ')) elements.push(<h4 key={`h4-${i}`} className="text-sm font-medium text-slate-700 mt-3 mb-1">{line.replace('### ', '')}</h4>);
    else if (line.startsWith('- ') || line.startsWith('* ')) elements.push(
      <li key={`li-${i}`} className="text-sm text-slate-600 ml-4 mb-1 list-disc">
        {line.replace(/^[-*]\s/, '').split('**').map((part, j) =>
          j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
        )}
      </li>
    );
    else if (line.trim()) elements.push(
      <p key={`p-${i}`} className="text-sm text-slate-600 mb-2">
        {line.split('**').map((part, j) =>
          j % 2 === 1 ? <strong key={`${i}-b${j}`} className="text-slate-800">{part}</strong> : <span key={`${i}-t${j}`}>{part}</span>
        )}
      </p>
    );
  }
  return <div>{elements}</div>;
};

export default function InterventionBacklogPage() {
  const { viewAsOrg } = useAuth();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const headers = useCallback(() => {
    const h = { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}` };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch the full backlog so hero stats reflect totals
      // regardless of the UI filter. Display-side filtering happens
      // in-memory below.
      const params = new URLSearchParams();
      if (viewAsOrg?.id) params.append('organization_id', viewAsOrg.id);
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog?${params}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch (err) {
      logger.error('Failed to load backlog:', err);
    }
    setLoading(false);
  }, [headers, viewAsOrg?.id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateItem = async (id, patch) => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog/${id}`, {
        method: 'PATCH',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setItems(prev => prev.map(it => it.id === id ? updated : it));
        if (activeItem?.id === id) setActiveItem(updated);
        return true;
      }
    } catch (err) {
      logger.error('Update failed:', err);
    }
    toast.error('Failed to update');
    return false;
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this intervention from the backlog?')) return;
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (res.ok) {
        setItems(prev => prev.filter(it => it.id !== id));
        if (activeItem?.id === id) setActiveItem(null);
        toast.success('Deleted');
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const saveNotes = async () => {
    if (!activeItem) return;
    setNotesSaving(true);
    await updateItem(activeItem.id, { notes: notesDraft });
    setNotesSaving(false);
    toast.success('Notes saved');
  };

  const [digestSending, setDigestSending] = useState(false);
  const sendMyDigest = async () => {
    setDigestSending(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog/send-digest`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ self_only: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Digest send failed');
      } else if (data.sent === 0) {
        toast.info('No open or recently completed items — nothing to email.');
      } else {
        toast.success(`Digest sent to ${data.to}`);
      }
    } catch {
      toast.error('Digest send failed');
    }
    setDigestSending(false);
  };

  // ── Per-user digest preferences ────────────────────────
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState(false);

  const loadPrefs = useCallback(async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog/preferences`, { headers: headers() });
      if (res.ok) setPrefs(await res.json());
    } catch (err) { logger.error('Failed to load prefs:', err); }
  }, [headers]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const savePrefs = async (patch) => {
    setPrefsSaving(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/intervention-backlog/preferences`, {
        method: 'PUT',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const data = await res.json();
        setPrefs(prev => ({ ...(prev || {}), ...data }));
        toast.success('Preferences saved');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save preferences');
      }
    } catch {
      toast.error('Failed to save preferences');
    }
    setPrefsSaving(false);
  };

  const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const FREQ_OPTIONS = [
    { value: 'off', label: 'Off — no digest emails' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
  ];

  // ── Program linker (reuses /api/segments/programs) ─────
  const [programs, setPrograms] = useState([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [linking, setLinking] = useState(false);

  const loadPrograms = useCallback(async (orgId) => {
    try {
      const url = new URL(`${API_URL}/api/segments/programs`);
      if (orgId) url.searchParams.append('organization_id', orgId);
      url.searchParams.append('limit', '100');
      const res = await fetch(url.toString(), { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setPrograms(data.programs || []);
      }
    } catch (err) { logger.error('Failed to load programs:', err); }
    setProgramsLoaded(true);
  }, [headers]);

  useEffect(() => {
    if (activeItem) {
      // Reset & reload per-item — programs are org-scoped.
      setProgramsLoaded(false);
      loadPrograms(activeItem.organization_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem?.id]);

  const linkProgram = async (programId) => {
    if (!activeItem) return;
    setLinking(true);
    await updateItem(activeItem.id, { linked_program_id: programId || null });
    setLinking(false);
    toast.success(programId ? 'Program linked' : 'Program unlinked');
  };

  const exportItemMarkdown = (item) => {
    const header = `# Action Plan — ${item.skill_name}\n\n` +
      `- **Status:** ${statusMeta(item.status).label}\n` +
      `- **Required proficiency:** ${item.required_level?.toFixed(1)}/5\n` +
      `- **Achieved proficiency:** ${item.achieved_level?.toFixed(1)}/5\n` +
      `- **Gap:** ${item.gap?.toFixed(2)} pts (${item.gap_pct}%)\n` +
      `- **Owner:** ${item.owner_name}\n` +
      `- **Saved:** ${item.created_at}\n\n---\n\n`;
    const notes = item.notes ? `\n\n---\n\n## Notes\n\n${item.notes}\n` : '';
    const blob = new Blob([header + (item.action_plan || '') + notes], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const slug = item.skill_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.href = url;
    a.download = `intervention-${slug}-${item.id.slice(-6)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const __pageTheme = getLightSectionTheme('PE');

  // ── Hero stats ──────────────────────────────────────────────
  // Derived entirely from `items` (always the full unfiltered
  // backlog). UI filter below narrows only the display list.
  const openItems = items.filter((i) => i.status === 'new' || i.status === 'in_progress');
  const newCount = items.filter((i) => i.status === 'new').length;
  const inProgressCount = items.filter((i) => i.status === 'in_progress').length;
  const avgGapPct = openItems.length
    ? openItems.reduce((s, i) => s + (Number(i.gap_pct) || 0), 0) / openItems.length
    : null;

  // Largest single open gap — powers the Avg-Gap tile's hint line
  // and lets users click straight into its detail dialog.
  const largestGapItem = openItems.length
    ? [...openItems].sort((a, b) => (b.gap_pct || 0) - (a.gap_pct || 0))[0]
    : null;

  // Completed in the last 30 days — updated_at is stamped on every
  // status change, so it doubles as a "last touched" timestamp for
  // completed items.
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const completed30d = items.filter((i) => {
    if (i.status !== 'completed') return false;
    const ts = i.updated_at ? new Date(i.updated_at).getTime() : 0;
    return ts > 0 && now - ts <= THIRTY_DAYS_MS;
  }).length;

  // "+3 this week" — new arrivals in the last 7 days, regardless
  // of current status. Gives a sense of inbound velocity.
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const arrivals7d = items.filter((i) => {
    const ts = i.created_at ? new Date(i.created_at).getTime() : 0;
    return ts > 0 && now - ts <= SEVEN_DAYS_MS;
  }).length;

  const openLargestClick = largestGapItem
    ? () => { setActiveItem(largestGapItem); setNotesDraft(largestGapItem.notes || ''); }
    : null;

  const fmtGapPct = (v) => (v == null ? '—' : `${Math.round(v)}%`);

  const heroTiles = items.length > 0 ? [
    {
      key: 'open',
      label: 'Open',
      value: openItems.length,
      hint: arrivals7d > 0 ? `+${arrivals7d} this week` : 'No new arrivals this week',
      headline: true,
      testId: 'backlog-stat-open',
    },
    {
      key: 'new',
      label: 'New',
      value: newCount,
      hint: 'Not yet started',
      testId: 'backlog-stat-new',
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      value: inProgressCount,
      hint: 'Actively being worked',
      testId: 'backlog-stat-in-progress',
    },
    {
      key: 'avg_gap',
      label: 'Avg Gap',
      value: avgGapPct != null ? `${avgGapPct.toFixed(1)}%` : '—',
      // Subtitle doubles as a CTA — click to jump to the largest
      // open gap's detail dialog.
      hint: largestGapItem ? (
        <span className="inline-flex items-center gap-1">
          <AlertTriangle className="h-2.5 w-2.5" />
          Largest: {largestGapItem.skill_name} {fmtGapPct(largestGapItem.gap_pct)}
        </span>
      ) : 'No open gaps',
      hintTone: largestGapItem ? 'cta' : 'default',
      onClick: openLargestClick || undefined,
      onClickTitle: largestGapItem ? 'Click to open the largest open gap' : undefined,
      testId: 'backlog-stat-avg-gap',
    },
    {
      key: 'completed',
      label: 'Completed (30d)',
      value: completed30d,
      hint: completed30d > 0 ? 'Recent wins — keep the momentum' : 'None closed in 30 days',
      testId: 'backlog-stat-completed',
    },
  ] : [];

  const heroStats = items.length > 0 ? (
    <MetricHeroStats tiles={heroTiles} testId="backlog-hero-stats" />
  ) : null;

  // Visible list = UI filter applied client-side.
  const displayedItems = filter === 'all' ? items : items.filter((i) => i.status === filter);

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Intervention Backlog"
        description="Prioritized actions to close People Engine gaps. Track each skill gap from first surfaced to closed — update status, attach learning programs, and export or email digests to owners."
        theme={__pageTheme}
        icon={Inbox}
        stats={heroStats}
        testId="intervention-backlog-hero"
      />
    )}>
    <div className="space-y-6" data-testid="intervention-backlog-page">
      
      <div className="flex items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="backlog-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchItems} className="gap-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={sendMyDigest}
          disabled={digestSending}
          className="gap-1.5"
          data-testid="send-my-digest-btn"
          title="Send yourself a summary email of your open interventions"
        >
          {digestSending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
          Email My Digest
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPrefsOpen(v => !v)}
          className="gap-1.5"
          data-testid="backlog-prefs-toggle"
          title="Email digest preferences"
        >
          <Settings className="h-3.5 w-3.5" />
          Preferences
        </Button>
        <span className="text-xs text-slate-400 ml-auto">
          {displayedItems.length} item{displayedItems.length === 1 ? '' : 's'}
          {filter !== 'all' && items.length !== displayedItems.length && (
            <span className="text-slate-300"> / {items.length} total</span>
          )}
        </span>
      </div>

      {prefsOpen && (
        <Card className="border-indigo-100 bg-indigo-50/30" data-testid="backlog-prefs-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="h-4 w-4 text-indigo-500" />
              Email Digest Preferences
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!prefs ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading preferences...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Frequency</label>
                  <Select
                    value={prefs.frequency}
                    onValueChange={(v) => savePrefs({ frequency: v })}
                    disabled={prefsSaving}
                  >
                    <SelectTrigger data-testid="prefs-frequency" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Preferred day</label>
                  <Select
                    value={String(prefs.preferred_day)}
                    onValueChange={(v) => savePrefs({ preferred_day: parseInt(v, 10) })}
                    disabled={prefsSaving || prefs.frequency === 'off'}
                  >
                    <SelectTrigger data-testid="prefs-day" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_LABELS.map((label, idx) => <SelectItem key={idx} value={String(idx)}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Preferred hour (UTC)</label>
                  <Select
                    value={String(prefs.preferred_hour)}
                    onValueChange={(v) => savePrefs({ preferred_hour: parseInt(v, 10) })}
                    disabled={prefsSaving || prefs.frequency === 'off'}
                  >
                    <SelectTrigger data-testid="prefs-hour" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {prefs && (
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Digest goes out at the weekly scheduler tick. "Every 2 weeks" and "Monthly" gate on your last delivery timestamp so you don't get more than one email per cadence window.
                {prefs.last_sent_at && ` Last sent: ${new Date(prefs.last_sent_at).toLocaleString()}.`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-6 w-6 text-indigo-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">No interventions saved yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Generate an Action Plan on the <strong>People Engine → Diagnostic → Capability</strong> page and click
              "Save to Program Backlog" to track it here.
            </p>
          </CardContent>
        </Card>
      ) : displayedItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-600">No items match the <strong>{statusMeta(filter).label}</strong> filter.</p>
            <button
              type="button"
              onClick={() => setFilter('all')}
              className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
              data-testid="backlog-filter-clear"
            >
              Show all statuses
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="backlog-items">
          {displayedItems.map(item => {
            const meta = statusMeta(item.status);
            return (
              <Card
                key={item.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setActiveItem(item); setNotesDraft(item.notes || ''); }}
                data-testid={`backlog-item-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Wand2 className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-slate-800 truncate">{item.skill_name}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.tone}`}>{meta.label}</span>
                        {item.linked_program_name && (
                          <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 flex items-center gap-1"
                            data-testid={`linked-program-badge-${item.id}`}
                            title={`Program ID: ${item.linked_program_id}`}
                          >
                            <LinkIcon className="h-3 w-3" />
                            {item.linked_program_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Gap <strong className="text-slate-700">{item.gap?.toFixed(2)}</strong> ({item.gap_pct}%)</span>
                        <span>Current {item.achieved_level?.toFixed(1)} / Required {item.required_level?.toFixed(1)}</span>
                        <span>Saved by {item.owner_name}</span>
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Select value={item.status} onValueChange={(v) => updateItem(item.id, { status: v })}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`backlog-status-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => exportItemMarkdown(item)} title="Export .md">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteItem(item.id)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        title="Delete"
                        data-testid={`backlog-delete-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!activeItem} onOpenChange={(o) => { if (!o) setActiveItem(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="backlog-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-indigo-500" />
              {activeItem?.skill_name}
              {activeItem && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ml-1 ${statusMeta(activeItem.status).tone}`}>
                  {statusMeta(activeItem.status).label}
                </span>
              )}
            </DialogTitle>
            {activeItem && (
              <DialogDescription className="text-xs text-slate-500">
                Gap {activeItem.gap?.toFixed(2)} pts · Saved by {activeItem.owner_name} · {new Date(activeItem.created_at).toLocaleString()}
              </DialogDescription>
            )}
          </DialogHeader>

          {activeItem && (
            <>
              <div className="prose prose-sm max-w-none pt-2">
                <MarkdownRenderer text={activeItem.action_plan} />
              </div>

              {/* Program linker */}
              <div className="mt-4 pt-3 border-t border-slate-100" data-testid="program-linker">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5 flex items-center gap-1.5">
                  <LinkIcon className="h-3.5 w-3.5 text-violet-500" />
                  Linked learning program
                </label>
                {activeItem.linked_program_name ? (
                  <div className="flex items-center justify-between gap-2 p-2.5 rounded-md bg-violet-50 border border-violet-200">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-violet-900 truncate">{activeItem.linked_program_name}</div>
                      <div className="text-xs text-violet-700/70 font-mono">{activeItem.linked_program_id}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-violet-600 hover:text-rose-600 hover:bg-rose-50 gap-1"
                      onClick={() => linkProgram(null)}
                      disabled={linking}
                      data-testid="unlink-program-btn"
                    >
                      <X className="h-3.5 w-3.5" /> Unlink
                    </Button>
                  </div>
                ) : (
                  <Select
                    value=""
                    onValueChange={(v) => linkProgram(v)}
                    disabled={linking || programs.length === 0}
                  >
                    <SelectTrigger className="text-sm" data-testid="link-program-select">
                      <SelectValue placeholder={programs.length === 0 ? 'No programs available for this org' : 'Select a program to link...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="font-medium">{p.name}</span>
                          <span className="text-slate-400 font-mono text-xs ml-2">{p.id}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100">
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">Notes</label>
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Progress notes, blockers, next checkpoints..."
                  className="text-sm min-h-[80px]"
                  data-testid="backlog-notes-textarea"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={saveNotes} disabled={notesSaving} data-testid="backlog-save-notes-btn">
                    {notesSaving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'Save Notes'}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => exportItemMarkdown(activeItem)}>
                    <Download className="h-3.5 w-3.5" /> Export .md
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => window.print()}>
                    <Printer className="h-3.5 w-3.5" /> Print
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
}
